import { React, useState, useContext, useMemo } from "react";
import { useParams, useLocation, NavLink } from "react-router-dom";
import { exportExcel } from "../../Config/config";
import FilingsFinancialTable from "../CFS/DetailsPage/CFSFinancial/Filings";
// import FormMCSite from '../CFS/DetailsPage/CFSFinancial/FormMCSite'
import ExcelDownloader from "../shared/ExcelDownloader";
import { routeNames } from "../../routeSegments";
import { CurrencyContext } from "../../Contexts/CurrencyContext";
import { UserContext } from "../../Contexts/UserContext";
import Constants from "../../Constants";
import ConfirmPopup from "../../Utils/ConfirmPopup";
import { Tooltip } from "antd";
import downloadIcon from "../../images/Download 3.svg";
import PL from "./FinancialComponents/PL";
import { TableHeaderContext } from "../../Contexts/TableHeaderContext";
import { useGetRatio } from "./FinancialComponents/ratios";
import Ratio from "./FinancialComponents/Ratio";
import updateFinacialIcon from "../../images/updateFinancials.png";
import { RaiseRequestContext } from "../../Contexts/RaiseRequestContext";
import Charges from "./FinancialComponents/Chages";
import { TableDataContext } from "../../Contexts/TableDataContext";

const FinancialTabs = ({ companyName, profile, data, menu, cfsId }) => {
  const location = useLocation();

  const allowedModulesStr = localStorage.getItem(
    Constants.localStorageKeys.allowedModules,
  );
  const allowedModulesList = JSON.parse(allowedModulesStr) ?? [];
  const [openTab, setOpenTab] = useState(1);
  const { currencyType, currency, setIsDetailed } = useContext(CurrencyContext);
  const { openRequestPopup } = useContext(RaiseRequestContext);

  const { isTrialExport, isStudent, isDirectoryUser } = useContext(UserContext);
  const [isDetail, setIsDetail] = useState(menu === "cfs");

  const { director } = useContext(TableDataContext);

  const { companyId } = useParams();
  const [alert, setAlert] = useState({ message: "", show: false });
  const companyProfileId = Number.parseInt(companyId, 10);
  const ratioData = useGetRatio(data);
  const financialsLen = data?.profit_loss || [];
  const hasCfsAccess = allowedModulesList.includes("cfs");
  const isRestrictedCfsUser = isDirectoryUser && !hasCfsAccess;
  const showFilings = !hasCfsAccess;
  const hasProfitLossData = financialsLen.length > 0;
  const hasCashFlowData = (data?.cash_flow?.length || 0) > 0;
  const hasBalanceSheetData = (data?.balance_sheet?.length || 0) > 0;
  const hasBasicFilings = (data?.basic_filings?.length || 0) > 0;
  const hasChargesData = (data.ioc?.length || 0) > 0;
  const showChargesTab = location.pathname.includes("cfs") && hasChargesData;
  const showFilingsTab = hasBasicFilings || (!isDirectoryUser && !hasCfsAccess);
  const shouldHideActions = [4, 5, 6].includes(openTab) || data?.length === 0;
  const canExport =
    isStudent === "false" && hasProfitLossData && isTrialExport === "true";
  const canShowUpdateFinancials = menu !== "cfs" && hasCfsAccess;
  const activeCompanyName =
    profile?.full_name || data?.cfs_profile?.full_name || companyName;
  const detailedCompanyName =
    data?.cfs_profile?.full_name || profile?.full_name;
  const hasMinimumStandalonePLData = (financials) => {
    const CURRENT_FY = Number(String(new Date().getFullYear()).slice(-2));
    const validFYs = financials
      .filter(
        (item) =>
          item.fin_type === "Standalone" &&
          Number(item.fy) >= 21 &&
          Number(item.fy) <= CURRENT_FY,
      )
      .map((item) => item.fy);

    const uniqueFYCount = new Set(validFYs).size;

    return uniqueFYCount >= 4;
  };
  const enableAiSummary =
    menu === "cfs" &&
    financialsLen?.length > 0 &&
    hasMinimumStandalonePLData(financialsLen);

  /**
   * The function `onDetailsClick` checks if a module called "cfs" is included in a list called
   * `allowedModulesList`, and if so, it sets the value of `isDetail` to the provided `value`. If "cfs"
   * is not included in `allowedModulesList`, it displays an alert message.
   * @param value - The value parameter is a variable that represents the value to be passed to the
   * setIsDetail function.
   */
  const onDetailsClick = (value) => {
    if (hasCfsAccess) {
      setIsDetail(value);
      setIsDetailed(true);
    } else {
      setAlert({
        show: true,
        message: Constants.cfsSubscriptionMsg,
        isAlertOnly: true,
        closeBtnName: "OK",
      });
    }
  };

  const onConfirmation = () => {
    setAlert({ show: false, message: "" });
  };

  const {
    plStandardKeys,
    balanceSheetKeys,
    cashFlowKeys,
    ratioKeys,
    nonDetailedPLKeys,
    nonDetailedBSKeys,
    nonDetailedCFKeys,
    nonDetailedRatioKeys,
  } = useContext(TableHeaderContext);

  const fileName = useMemo(() => {
    const exportNameMap = {
      1: Constants.exportNames.plStatement,
      2: Constants.exportNames.balanceSheet,
      3: Constants.exportNames.cashflow,
      4: Constants.exportNames.ratios,
      7: Constants.exportNames.ratios,
    };

    return (exportNameMap[openTab] || Constants.exportNames.plStatement)(
      activeCompanyName,
    );
  }, [activeCompanyName, openTab]);

  const commonFinancialProps = {
    profile,
    isDetails: isDetail,
    setIsDetail: onDetailsClick,
    isExport: isTrialExport,
    companyLabel: profile?.is_xbrl,
    companyName,
    fCompanyName: detailedCompanyName,
    menu,
    cfsId,
  };

  return (
    <>
      <div>
        <div className="mx-4">
          <div className=" bg-white border overflow-hidden sm:rounded-lg shadow-[0_2px_6px_0_rgba(0,0,0,0.08)]">
            <div className={`py-3 px-5 border-b border-b-[#F1F1F1]`}>
              <div className="mt-3 lg:mt-0 flex md:flex justify-between space-y-2 md:space-y-0">
                <div className="flex md:flex justify-start items-center">
                  <h3 className="text-[14pt] pr-2 md:pr-0 leading-[19pt] text-[#333333] lg:text-[17px] font-semibold  lg:leading-[22px] lg:mt-3 flex items-end">
                    Financials
                  </h3>
                  {enableAiSummary && (
                    <NavLink
                      className="ml-2 relative px-[0.25rem] max-h-[20px] text-xs lg:text-[12px] lg:mt-3 font-semibold border border-[#0077B7] bg-[#0077B7] rounded-sm text-white cursor-pointer"
                      to={routeNames.cfsAISummary(companyId)}
                      target="_blank"
                    >
                      <span className="text-xs text-red-700 absolute -top-3 -right-7">
                        Beta
                      </span>
                      AI Summary
                    </NavLink>
                  )}
                </div>
                {shouldHideActions ? (
                  <div></div>
                ) : (
                  <span className="flex items-center md:justify-center justify-between md:space-x-4 space-x-2 md:pr-2">
                    <div className="flex space-x-4">
                      {canExport ? (
                        <>
                          <ExcelDownloader
                            url={exportExcel}
                            fileName={fileName}
                            data={{
                              export_from: "detail",
                              module_type: "investments",
                              section: "financials",
                              type: 5,
                              is_detailed: isDetail,
                              currency_cd: currencyType,
                              display_in: currency,
                              company_id:
                                menu === "cfs" ? [companyProfileId] : [cfsId],
                            }}
                          >
                            <Tooltip title="Export">
                              <img
                                src={downloadIcon}
                                className="cursor-pointer h-[26px] w-[24px]"
                                alt=""
                              />
                            </Tooltip>
                          </ExcelDownloader>
                          {canShowUpdateFinancials && (
                            <Tooltip title="Update financials">
                              <img
                                src={updateFinacialIcon}
                                className="cursor-pointer  h-[25px] w-[24px]"
                                alt=""
                                onClick={() =>
                                  openRequestPopup("UpdateFinancials", "CFS")
                                }
                              />
                            </Tooltip>
                          )}
                        </>
                      ) : (
                        <></>
                      )}
                    </div>
                  </span>
                )}
              </div>
            </div>
            {!hasProfitLossData ? (
              <div className="h-28 w-full">
                <div className="w-full h-full flex items-center justify-center">
                  <p className="px-5 text-[14px]">
                    Data not found. Please{" "}
                    <button
                      className="text-[#A5711A] hover:underline"
                      onClick={() => openRequestPopup("financial", "CFS")}
                    >
                      Click Here
                    </button>{" "}
                    to alert Venture Intelligence about this. Thanks
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Fiancial  Header Section */}
                <div className="justify-between flex">
                  <div className="flex flex-row overflow-x-auto space-x-9 scrollbar px-5 border-b border-b-[#F1F1F1] scrollbar-remove">
                    <a
                      className={
                        "whitespace-nowrap  py-3 text-[12pt] leading-[17pt] lg:text-[14px] lg:leading-[15px] block cursor-pointer " +
                        (openTab === 1
                          ? "text-[#2B2A29] border-b-[3px] border-[#F1CA75] font-semibold"
                          : "  text-[#2B2A29] ")
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenTab(1);
                      }}
                      data-toggle="tab"
                      role="tablist"
                    >
                      Profit & Loss Statement
                    </a>

                    <a
                      className={
                        `whitespace-nowrap  py-3 text-[12pt] leading-[17pt]  lg:text-[14px] block lg:leading-[15px]  cursor-pointer` +
                        (openTab === 2
                          ? "text-[#2B2A29] border-b-[3px] border-[#F1CA75] font-semibold"
                          : " text-[#2B2A29]  ")
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenTab(2);
                      }}
                      data-toggle="tab"
                      role="tablist"
                      // disabled={isDirectoryUser}
                    >
                      Balance Sheet
                    </a>

                    <button
                      className={
                        `whitespace-nowrap  py-3 text-[12pt] leading-[17pt]  lg:text-[14px]   block lg:leading-[15px] ${!hasCashFlowData ? "cursor-not-allowed" : "cursor-pointer"} ` +
                        (openTab === 3
                          ? "text-[#2B2A29] border-b-[3px] border-[#F1CA75] font-semibold"
                          : " text-[#2B2A29]  ")
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenTab(3);
                      }}
                      disabled={!hasCashFlowData}
                      data-toggle="tab"
                      role="tablist"
                    >
                      Cash Flow
                    </button>

                    <button
                      className={
                        `whitespace-nowrap  py-3 text-[12pt] leading-[17pt]  lg:text-[14px]   block lg:leading-[15px] ${isRestrictedCfsUser || !hasBalanceSheetData ? "cursor-not-allowed" : "cursor-pointer"} ` +
                        (openTab === 4
                          ? "text-[#2B2A29] border-b-[3px] border-[#F1CA75] font-semibold"
                          : " text-[#2B2A29]  ")
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenTab(4);
                      }}
                      data-toggle="tab"
                      role="tablist"
                      disabled={
                        !hasBalanceSheetData ||
                        isRestrictedCfsUser ||
                        !hasCfsAccess
                      }
                    >
                      Ratios
                    </button>
                    {showFilingsTab ? (
                      <button
                        className={
                          `whitespace-nowrap py-3 text-[12pt] leading-[17pt] lg:text-[14px]   block lg:leading-[15px] ${isRestrictedCfsUser || !hasBasicFilings || showFilings ? "cursor-not-allowed" : "cursor-pointer"}` +
                          (openTab === 5
                            ? "text-[#2B2A29] border-b-[3px] border-[#F1CA75] font-semibold"
                            : " text-[#2B2A29]  ")
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          setOpenTab(5);
                        }}
                        disabled={
                          !hasBasicFilings ||
                          isRestrictedCfsUser ||
                          !hasCfsAccess ||
                          showFilings
                        }
                        data-toggle="tab"
                        role="tablist"
                      >
                        Filings
                      </button>
                    ) : (
                      ""
                    )}
                    {showChargesTab && (
                      <button
                        className={
                          `whitespace-nowrap  py-3 text-[12pt] leading-[17pt]  lg:text-[14px]   block lg:leading-[15px] ${isRestrictedCfsUser || !hasChargesData ? "cursor-not-allowed" : "cursor-pointer"} ` +
                          (openTab === 7
                            ? "text-[#2B2A29] border-b-[3px] border-[#F1CA75] font-semibold"
                            : " text-[#2B2A29]  ")
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          setOpenTab(7);
                        }}
                        data-toggle="tab"
                        role="tablist"
                        disabled={
                          !hasChargesData ||
                          isRestrictedCfsUser ||
                          !hasCfsAccess
                        }
                      >
                        Charges
                      </button>
                    )}

                    {/* 
                  <button
                    className={
                      `whitespace-nowrap py-3 text-[12pt] leading-[17pt] lg:text-[14px]   block lg:leading-[15px] ${(isDirectoryUser && !allowedModulesList.includes('cfs')) || (!allowedModulesList.includes('cfs'))  ? 'cursor-not-allowed':'cursor-pointer'} ` +
                      (openTab === 6
                        ? "text-[#2B2A29] border-b-[3px] border-[#F1CA75] font-semibold"
                        : " text-[#2B2A29]  ")
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenTab(6);
                    }}
                    data-toggle="tab"
                    href="#link6"
                    role="tablist"
                    disabled={(isDirectoryUser && !allowedModulesList.includes('cfs')) || (!allowedModulesList.includes('cfs')) }
                  >
                    {/* <Link to="/financial/mca-site"> /}
                    From MCA Site
                    {/* </Link> /}
                  </button> 
                  */}
                  </div>
                </div>
                <div className={openTab === 1 ? "block" : "hidden"} id="link1">
                  <PL
                    data={data?.profit_loss}
                    openTab={openTab}
                    cfsFinKeys={isDetail ? plStandardKeys : nonDetailedPLKeys}
                    {...commonFinancialProps}
                  />
                </div>

                {/* Balance Sheet */}

                <div className={openTab === 2 ? "block" : "hidden"} id="link2">
                  <PL
                    data={data?.balance_sheet}
                    openTab={openTab}
                    cfsFinKeys={isDetail ? balanceSheetKeys : nonDetailedBSKeys}
                    {...commonFinancialProps}
                  />
                </div>

                {/* Cash Flow */}
                <div className={openTab === 3 ? "block" : "hidden"} id="link3">
                  <PL
                    data={data?.cash_flow}
                    openTab={openTab}
                    cfsFinKeys={isDetail ? cashFlowKeys : nonDetailedCFKeys}
                    {...commonFinancialProps}
                  />
                </div>

                <div className={openTab === 4 ? "block" : "hidden"} id="link4">
                  <Ratio
                    data={ratioData}
                    isDetails={isDetail}
                    setIsDetail={onDetailsClick}
                    openTab={openTab}
                    isRatio={true}
                    companyLabel={profile?.is_xbrl}
                    cfsFinKeys={isDetail ? ratioKeys : nonDetailedRatioKeys}
                    isActive={openTab === 4}
                  />
                </div>
                {hasChargesData && (
                  <div
                    className={openTab === 7 ? "block" : "hidden"}
                    id="link7"
                  >
                    <Charges
                      data={data?.ioc}
                      isDetails={isDetail}
                      setIsDetail={onDetailsClick}
                      openTab={openTab}
                      companyLabel={profile?.is_xbrl}
                      companyProfileId={companyProfileId}
                      fCompanyName={detailedCompanyName}
                    />
                  </div>
                )}
                <div className={openTab === 5 ? "block" : "hidden"} id="link5">
                  <FilingsFinancialTable
                    filings={data?.basic_filings}
                    cfsId={data?.cfs_profile?.pk}
                  />
                </div>
              </>
            )}
            {/* <div className={openTab === 6 ? "block" : "hidden"} id="link6">
              <FormMCSite />
            </div> */}
          </div>
        </div>
        {/* {open && <ChatPopup page="pe" component="financial" data={false} /> } */}
        {<ConfirmPopup {...alert} onChange={onConfirmation} />}
      </div>
    </>
  );
};

export default FinancialTabs;